/**
 * SigTDD Forgot Password Page
 *
 * Request password reset email.
 */

import { useState } from 'react';
import './AuthPages.css';

const MailIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

interface ForgotPasswordPageProps {
  onClose?: () => void;
  onLogin?: () => void;
}

export default function ForgotPasswordPage({ onClose, onLogin }: ForgotPasswordPageProps) {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const emailValue = formData.get('email') as string;
    setEmail(emailValue);

    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailValue }),
      });

      const data = await res.json();

      if (res.ok) {
        setSent(true);
      } else {
        setError(data.error || 'Failed to send reset email. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    }

    setLoading(false);
  };

  // Success state - email sent
  if (sent) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-header">
            <img src="/sigtdd.svg" alt="" className="auth-logo" />
            <div className="auth-status-icon auth-status-success">
              <MailIcon />
            </div>
            <h1>Check your email</h1>
            <p>
              If an account exists for <strong>{email}</strong>, we've sent a password reset link.
            </p>
          </div>

          <div className="auth-hint" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            Didn't receive the email? Check your spam folder or try again.
          </div>

          <button className="auth-submit" onClick={onLogin}>
            Back to sign in
          </button>

          {onClose && (
            <div className="auth-back">
              <button className="auth-back-link" onClick={onClose}>
                Back to App
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Form state
  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <img src="/sigtdd.svg" alt="" className="auth-logo" />
          <h1>Reset your password</h1>
          <p>Enter your email and we'll send you a reset link</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="auth-error">{error}</div>
          )}

          <div className="auth-field">
            <label htmlFor="email">Email address</label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="you@example.com"
              required
              autoComplete="email"
              autoFocus
            />
          </div>

          <button type="submit" className="auth-submit" disabled={loading}>
            {loading ? 'Sending...' : 'Send reset link'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Remember your password?{' '}
            <button className="auth-link-btn" onClick={onLogin}>
              Sign in
            </button>
          </p>
        </div>

        {onClose && (
          <div className="auth-back">
            <button className="auth-back-link" onClick={onClose}>
              Back to App
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
